/**
 * A field saved where it is drawn (CASE-026): leaving it or Enter saves it,
 * Esc puts back the saved value, and a newer version waits at the field with
 * Keep mine and Load theirs (R4). A choice saves when it is left, like text,
 * never on its own (O8).
 */
import type { KeyboardEvent } from "react";

import type { Conflict, FactField, FieldAria } from "@/components/ui/box.schema";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";

import { peopleWords as words } from "../people-copy";
import type { FieldSaves } from "./commit";

export interface FieldOption {
  value: string;
  label: string;
}

export interface FieldSpec<R, F extends string> {
  /** Unique on the page: the kit names the control by it. */
  id: string;
  field: F;
  label: string;
  /** Their value in words, as the field shows it. */
  theirs: (record: R) => string;
  note?: string;
  type?: "text" | "tel" | "email";
  multiline?: boolean;
  /** A choice: `empty` is the option for no value. */
  options?: { empty: string; choices: readonly FieldOption[] };
}

const focusField = (id: string) =>
  document.querySelector<HTMLElement>(`[data-field="${id}"] :is(input, select, textarea)`)?.focus();

export function savedField<R, F extends string>(saves: FieldSaves<R, F>, spec: FieldSpec<R, F>): FactField {
  const { id, field, label } = spec;
  const newer = saves.theirs(field);
  const conflict: Conflict | undefined = newer
    ? {
        sentence: words.changedSinceYouReadIt,
        facts: [{ label, theirs: spec.theirs(newer) || words.notRecorded }],
        keepMine: { label: words.keepMine, run: () => { saves.keepMine(field); focusField(id); } },
        loadNewer: { label: words.loadTheirs, run: () => { saves.loadTheirs(field); focusField(id); } },
      }
    : undefined;
  const unsettled = saves.unsettled(field);
  const keys = (event: KeyboardEvent<HTMLElement>) => {
    if (unsettled) return;
    if (event.key === "Enter" && !spec.multiline) {
      event.preventDefault();
      saves.save(field);
    }
    if (event.key === "Escape") {
      event.preventDefault();
      saves.restore(field);
    }
  };
  const common = {
    dir: "auto" as const,
    value: saves.value(field),
    onBlur: () => saves.save(field),
    onKeyDown: keys,
  };
  const render = (labelledBy: string, aria: FieldAria) => {
    if (spec.options)
      return (
        <NativeSelect aria-labelledby={labelledBy} {...aria} {...common} disabled={unsettled} onChange={(event) => saves.set(field, event.target.value)}>
          <option value="">{spec.options.empty}</option>
          {spec.options.choices.map((choice) => (
            <option key={choice.value} value={choice.value}>{choice.label}</option>
          ))}
        </NativeSelect>
      );
    if (spec.multiline)
      return <Textarea aria-labelledby={labelledBy} rows={2} {...aria} {...common} readOnly={unsettled} onChange={(event) => saves.set(field, event.target.value)} />;
    return (
      <Input
        aria-labelledby={labelledBy}
        type={spec.type ?? "text"}
        autoComplete="off"
        spellCheck={spec.type === "email" ? false : undefined}
        {...aria}
        {...common}
        readOnly={unsettled}
        onChange={(event) => saves.set(field, event.target.value)}
      />
    );
  };
  return { id, label, note: spec.note, conflict, save: saves.status(field), render };
}
